import { useState } from 'react';
import { AppModal, ConfirmDialog } from '@/components/modals';
import { StatusPill } from '@/components/common/StatusPill';
import { ClientDetailsCard } from '@/features/clients-portal/components/ClientDetailsCard';
import { ClientEditor } from '@/features/clients-portal/components/ClientEditor';
import { ClientsPortalFilterPanel } from '@/features/clients-portal/components/ClientsPortalFilterPanel';
import { ClientsPortalHeader } from '@/features/clients-portal/components/ClientsPortalHeader';
import { ClientsPortalSummaryCards } from '@/features/clients-portal/components/ClientsPortalSummaryCards';
import { ClientsPortalTable } from '@/features/clients-portal/components/ClientsPortalTable';
import { useClientsPortalData } from '@/features/clients-portal/useClientsPortalData';

export function ClientsPortalPage() {
  const {
    employeeId,
    currentUser,
    loading,
    error, clearError,
    clients,
    allClientsCount,
    canManageClients,
    isSuperAdmin,
    submitting,
    message, clearMessage,
    filters,
    updateFilters,
    resetFilters,
    refresh,
    syncClients,
    clientOptions,
    serviceOptions,
    editor,
    openEditor,
    closeEditor,
    updateEditor,
    submitEditor,
    deleteClient,
    detailsClient,
    setDetailsClient
  } = useClientsPortalData();
  const [deleteTarget, setDeleteTarget] = useState(null);

  return (
    <section className="page-card clients-portal-page">
      {submitting && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg style={{ animation: 'wt-spin 1s linear infinite', width: '48px', height: '48px', color: '#6941C6' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25"></circle>
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p style={{ marginTop: '20px', fontWeight: '600', fontSize: '18px', color: '#111827' }}>Fetching Client Data...</p>
          <p style={{ marginTop: '8px', fontSize: '14px', color: '#4B5563' }}>Please wait while we securely sync with the latest Google Sheet.</p>
        </div>
      )}

      <ClientsPortalHeader
        employeeLabel={`${currentUser?.['Employee Name'] || currentUser?.Name || 'Employee'}${employeeId ? ` | ${employeeId}` : ''}`}
        onRefresh={refresh}
        canSync={canManageClients}
        onSync={syncClients}
        submitting={submitting}
        showAddClient={isSuperAdmin}
        onAddClient={() => openEditor()}
      />

      {error ? (
        <div className="dashboard-banner dashboard-banner--error">
          <span>{error}</span>
          <button type="button" className="dashboard-banner__close" onClick={clearError}>OK</button>
        </div>
      ) : null}

      {message ? (
        <div className={`dashboard-banner${message.tone === 'danger' ? ' dashboard-banner--error' : ''}`}>
          <StatusPill tone={message.tone === 'danger' ? 'danger' : 'success'}>
            {message.tone === 'danger' ? 'Update failed' : 'Update complete'}
          </StatusPill>
          <span>{message.text}</span>
        
          <button type="button" className="dashboard-banner__close" onClick={clearMessage}>OK</button>
        </div>
      ) : null}

      <ClientsPortalSummaryCards visibleCount={clients.length} totalCount={allClientsCount} isAdmin={canManageClients} />

      <ClientsPortalFilterPanel
        filters={filters}
        clientOptions={clientOptions}
        serviceOptions={serviceOptions}
        onUpdateFilters={updateFilters}
        onResetFilters={resetFilters}
      />

      <ClientDetailsCard client={detailsClient} onClose={() => setDetailsClient(null)} />
      {editor.open ? (
        <AppModal
          title={editor.client.Client_Id ? `Edit Client: ${editor.client.Client_Id}` : 'Add Client'}
          onClose={closeEditor}
          width="980px"
        >
          <ClientEditor editor={editor} submitting={submitting} onUpdate={updateEditor} onClose={closeEditor} onSubmit={submitEditor} />
        </AppModal>
      ) : null}

      <ClientsPortalTable
        clients={clients}
        allClientsCount={allClientsCount}
        isAdmin={canManageClients}
        submitting={submitting}
        onOpenDetails={setDetailsClient}
        onOpenEditor={openEditor}
    onRequestDeactivate={setDeleteTarget}
      />
      {deleteTarget ? <ConfirmDialog title="Delete Client" message={`Delete ${deleteTarget.Client_Id}? This permanently removes the client portal record and login.`} busy={submitting} onClose={() => setDeleteTarget(null)} onConfirm={async () => { await deleteClient(deleteTarget.Client_Id); setDeleteTarget(null); }} confirmLabel="Delete Client" /> : null}
    </section>
  );
}
